import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import bgAuthBridge, { AuthUser as BGAuthUser } from '../services/bgAuthBridge';
import billingApi from '../services/billingApi';
import { isAuthConfigured, signOutUser } from '../services/authService';
import { initRelayAuth } from '../services/safariRelayAuth';
import { signInWithChromeIdentity } from '../services/chromeIdentityAuth';
import { signInWithAppleIdentity } from '../services/appleIdentityAuth';
import { signInAppleNative, signInGoogleNative, signOutNative, handoffSessionToNative, NativeAuthResult } from '../services/nativeAppAuth';
import {
  signUpWithEmail,
  signInWithEmail,
  sendReset,
  resendVerification as resendVerificationEmail,
  refreshVerificationStatus,
  EmailAuthResult,
} from '../services/emailPasswordAuth';
import { auth } from '../config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth/web-extension';
import { clearCachedEntitlement } from '../services/entitlementCache';
import { clearUserScopedStorageAsync } from '../services/storage';
import { isNativeApp } from '../utils/nativeApp';
import { log, warn, error as logError } from '../utils/logger';
import { ext, hasExtensionRuntime } from '../utils/browserApi';

const AUTH_USER_KEY = 'auth.user';
const AUTH_STATUS_KEY = 'auth.status';
const AUTH_FALLBACK_KEY = 'auth.user.fallback';
const REQUIRE_FRESH_LOGIN = process.env.REACT_APP_REQUIRE_FRESH_LOGIN === 'true';

const getFallbackUser = (): BGAuthUser | null => {
  try {
    const raw = localStorage.getItem(AUTH_FALLBACK_KEY);
    return raw ? (JSON.parse(raw) as BGAuthUser) : null;
  } catch {
    return null;
  }
};

const setFallbackUser = (user: BGAuthUser | null): void => {
  try {
    if (user) {
      localStorage.setItem(AUTH_FALLBACK_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_FALLBACK_KEY);
    }
  } catch {
    // ignore local storage errors
  }
};

const getUserFromExtStorage = async (): Promise<BGAuthUser | null> => {
  if (!(ext as any)?.storage?.local?.get) return null;
  return new Promise((resolve) => {
    ext.storage.local.get([AUTH_USER_KEY], (res: Record<string, BGAuthUser | null>) => {
      if (ext.runtime?.lastError) {
        resolve(null);
        return;
      }
      resolve(res?.[AUTH_USER_KEY] || null);
    });
  });
};

/**
 * Persist the signed-in user to the service worker's chrome.storage via the
 * `auth:setUser` message. This is the cross-tab source of truth: bgAuthBridge
 * .getUser() reads it on every new extension page. MUST be called for ALL
 * sign-in paths (Google, Apple, AND the Safari relay/redirect resume) — if it
 * isn't, the bridge reports `signed-out` on the next tab and the session is
 * lost even though Firebase/localStorage still hold it.
 */
const persistUserToBackground = async (user: BGAuthUser): Promise<void> => {
  if (!(ext as any)?.runtime?.sendMessage) return;
  try {
    const isExtCtx = hasExtensionRuntime();
    const message = { type: 'auth:setUser', payload: { user } };
    const EXTENSION_ID: string = (ext as any).runtime?.id || '';
    await new Promise<void>((resolve, reject) => {
      const done = (res?: any) => {
        if (ext.runtime?.lastError) {
          reject(new Error((ext.runtime.lastError as any).message || 'Failed to persist auth user'));
          return;
        }
        if (res?.success === false) {
          reject(new Error(res?.error || 'Failed to persist auth user'));
          return;
        }
        resolve();
      };
      if (isExtCtx) {
        ext.runtime.sendMessage(message, done);
      } else {
        ext.runtime.sendMessage(EXTENSION_ID, message, done);
      }
    });
  } catch (err) {
    warn('[AuthContext] Failed to persist auth user to background storage:', err);
  }
};

export interface PendingVerification {
  email: string;
}

interface AuthContextValue {
  user: BGAuthUser | null;
  loading: boolean;
  error: string | null;
  /**
   * Raw Firebase error code (e.g. 'auth/invalid-credential') for the current
   * error, so surfaces can translate it via services/authErrorMap instead of
   * rendering the SDK's untranslated English `error` string.
   */
  errorCode: string | null;
  signInGoogle: () => Promise<{ user: BGAuthUser | null; error?: string | undefined }>;
  signInApple: () => Promise<{ user: BGAuthUser | null; error?: string | undefined }>;
  /**
   * Create an email/password account. NEVER signs the user in: the account is
   * blocked until the verification link is clicked, so this resolves with
   * `pendingVerification` set and `user` null.
   */
  signUpEmail: (email: string, password: string, displayName?: string) => Promise<EmailAuthResult>;
  /**
   * Sign in with email/password. An unverified account resolves with
   * `pendingVerification` rather than a user, for the same reason as above.
   */
  signInEmail: (email: string, password: string) => Promise<EmailAuthResult>;
  sendPasswordReset: (email: string) => Promise<{ ok: boolean; error?: string; errorCode?: string }>;
  resendVerification: () => Promise<{ ok: boolean; error?: string; errorCode?: string }>;
  /** Re-check verification; promotes the user to signed-in once it succeeds. */
  checkVerification: () => Promise<{ verified: boolean }>;
  /** Set while an account exists but has not confirmed its email address. */
  pendingVerification: PendingVerification | null;
  clearPendingVerification: () => void;
  /**
   * Drop the current error. Needed because `error` outlives the surface that
   * produced it: a failed password attempt would otherwise still be showing
   * after the user backs out to the email step and starts typing a new address.
   */
  clearAuthError: () => void;
  signOut: () => Promise<void>;
  /**
   * Delete the signed-in account (backend `deleteAccount` mutation), then end
   * the session on every surface. Throws ONLY when the backend deletion failed
   * — the session is left intact so the caller can show a retryable error.
   */
  deleteAccount: () => Promise<void>;
  isReady: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * iOS native app only (isNativeApp()): apply the result of a native bridge
 * sign-in (Apple/Google) to context state. Mirrors the Chrome success/error
 * handling below exactly, plus the protocol's cancellation rule — a dismissed
 * native sheet is a silent no-op, never an error toast. Dead code on Chrome.
 */
const applyNativeSignIn = async (
  result: NativeAuthResult,
  setters: {
    setUser: (u: BGAuthUser | null) => void;
    setError: (e: string | null) => void;
    setLoading: (l: boolean) => void;
  },
): Promise<{ user: BGAuthUser | null; error?: string | undefined }> => {
  const { setUser, setError, setLoading } = setters;
  if (result.user) {
    await persistUserToBackground(result.user);
    setFallbackUser(result.user);
    setUser(result.user);
    setLoading(false);
    return { user: result.user };
  }
  if (result.cancelled) {
    setLoading(false);
    return { user: null };
  }
  const errStr = result.error || 'Sign-in failed';
  logError('[AuthContext] Native sign-in failed:', errStr);
  setError(errStr);
  setLoading(false);
  return { user: null, error: errStr };
};

/**
 * Look for an unverified password session, bounded so a cold start is never held
 * up by it. AuthContext does not subscribe to onAuthStateChanged (all of its
 * state comes from the bridge and the storage fallbacks), so this is a one-shot
 * peek rather than a subscription.
 */
const PENDING_PEEK_TIMEOUT_MS = 2000;

const peekPendingVerification = (): Promise<PendingVerification | null> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (value: PendingVerification | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { unsubscribe(); } catch { /* not yet assigned */ }
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), PENDING_PEEK_TIMEOUT_MS);

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (!fbUser || fbUser.emailVerified) return finish(null);
      const isPassword = fbUser.providerData.some(
        (entry: { providerId: string }) => entry.providerId === 'password',
      );
      finish(isPassword ? { email: fbUser.email || '' } : null);
    });
  });

/**
 * Apply a successful WEB sign-in (Google, Apple, or email/password) to context
 * state.
 *
 * Every sign-in path must go through here. Two steps are easy to forget and both
 * fail silently:
 *
 *   persistUserToBackground — without it the bridge reports `signed-out` on the
 *     next extension tab and the session is lost even though Firebase still
 *     holds it (see the docblock on that function).
 *
 *   handoffSessionToNative — without it an iOS sign-in leaves the Swift side
 *     with no Firebase session, and StoreKit reconciliation plus /apple/verify
 *     break for that user. Best-effort: never fails the sign-in.
 */
const applyWebSignIn = async (
  user: BGAuthUser,
  setters: {
    setUser: (u: BGAuthUser | null) => void;
    setLoading: (l: boolean) => void;
  },
): Promise<{ user: BGAuthUser }> => {
  await persistUserToBackground(user);
  setFallbackUser(user);
  setters.setUser(user);

  if (isNativeApp() && auth.currentUser) {
    await handoffSessionToNative(auth.currentUser);
  }

  setters.setLoading(false);
  return { user };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<BGAuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    setIsReady(isAuthConfigured());

    (async () => {
      try {
        // Resume Safari relay auth (stored by detectSafariRelayToken() in App.tsx).
        const relayResult = await initRelayAuth();
        if (relayResult.user && mounted) {
          await persistUserToBackground(relayResult.user);
          setUser(relayResult.user);
          setFallbackUser(relayResult.user);
          setLoading(false);
          return;
        }

        // (Firebase redirect-result resume removed: signInWithRedirect/getRedirectResult
        // are not in the MV3-safe web-extension build. Safari uses the relay above;
        // Chrome uses launchWebAuthFlow — neither relies on getRedirectResult.)

        if (REQUIRE_FRESH_LOGIN) {
          setUser(null);
          setFallbackUser(null);
          try {
            if ((ext as any)?.storage?.local?.remove) {
              ext.storage.local.remove([AUTH_USER_KEY, AUTH_STATUS_KEY]);
            }
          } catch {
            // ignore
          }
          return;
        }

        const res = await bgAuthBridge.getUser();
        if (!mounted) return;
        let nextUser: BGAuthUser | null = null;

        if (res?.user) {
          nextUser = res.user;
        } else {
          // The bridge can transiently report `signed-out` right after a FIRST
          // sign-in: the service worker's chrome.storage.local.set() write may not
          // have landed yet, so this read races the write. A persisted fallback is
          // only ever written on a real sign-in and cleared on sign-out, so trust it
          // over a possibly-stale signed-out. This removes the first-sign-in blank /
          // SignInScreen flash that a manual refresh used to "fix". (Previously a
          // `signed-out` response short-circuited to null and ignored the fallback.)
          nextUser = (await getUserFromExtStorage()) || getFallbackUser();
        }

        // No session anywhere — but an email/password account that signed up and
        // never opened the verification link DOES have a live Firebase session,
        // just one we deliberately refuse to promote. Without this peek that user
        // lands on a bare sign-in screen with no idea a link is waiting, which is
        // the most likely drop-off point in the whole flow.
        if (!nextUser) {
          const pending = await peekPendingVerification();
          if (!mounted) return;
          if (pending) setPendingVerification(pending);
        }

        setUser(nextUser);
        // Only persist a real user — never let a race-stale null wipe the fallback
        // that a concurrent first sign-in just wrote synchronously.
        if (nextUser) setFallbackUser(nextUser);
      } catch (e) {
        if (!mounted) return;
        const fallbackUser = (await getUserFromExtStorage()) || getFallbackUser();
        setUser(fallbackUser);
        setFallbackUser(fallbackUser);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const storageHandler = (changes: any, areaName: string) => {
      if (areaName !== 'local') return;
      const authChange = changes?.auth?.newValue || changes?.['auth.user']?.newValue || null;
      if (authChange) {
        setUser(authChange);
        setFallbackUser(authChange);
      } else if (changes?.['auth.user'] && changes['auth.user'].newValue == null) {
        setUser(null);
        setFallbackUser(null);
      }
    };

    try {
      ext.storage.onChanged.addListener(storageHandler);
    } catch {
      // ignore if ext.storage not available in this environment
    }

    return () => {
      mounted = false;
      try { ext.storage.onChanged.removeListener(storageHandler); } catch { /* Runtime may be unavailable. */ }
    };
  }, []);

  const signInGoogle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // iOS native app: ASWebAuthenticationSession via the bridge, then
      // signInWithCredential — no popups, no chrome.identity.
      if (isNativeApp()) {
        log('[AuthContext] Starting Google sign-in via native bridge...');
        const nativeResult = await signInGoogleNative();
        return await applyNativeSignIn(nativeResult, { setUser, setError, setLoading });
      }

      log('[AuthContext] Starting Google sign-in via Identity API...');
      const result = await signInWithChromeIdentity();

      log('[AuthContext] Sign-in result:', result);

      if (result && result.user) {
        log('[AuthContext] Sign-in successful, user:', result.user);
        return await applyWebSignIn(result.user, { setUser, setLoading });
      } else {
        const errStr = result?.error || 'Sign-in failed';
        logError('[AuthContext] Sign-in failed:', errStr);
        setError(errStr);
        setLoading(false);
        return { user: null, error: errStr };
      }
    } catch (e) {
      const errStr = e instanceof Error ? e.message : String(e || 'Sign-in failed');
      logError('[AuthContext] Sign-in exception:', errStr, e);
      setError(errStr);
      setLoading(false);
      return { user: null, error: errStr };
    }
  }, []);

  const signInApple = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // iOS native app: ASAuthorizationController via the bridge.
      if (isNativeApp()) {
        log('[AuthContext] Starting Apple sign-in via native bridge...');
        const nativeResult = await signInAppleNative();
        return await applyNativeSignIn(nativeResult, { setUser, setError, setLoading });
      }

      log('[AuthContext] Starting Apple sign-in...');
      const result = await signInWithAppleIdentity();

      if (result && result.user) {
        return await applyWebSignIn(result.user, { setUser, setLoading });
      }

      const errStr = result?.error || 'Apple sign-in failed';
      setError(errStr);
      setLoading(false);
      return { user: null, error: errStr };
    } catch (e) {
      const errStr = e instanceof Error ? e.message : String(e || 'Apple sign-in failed');
      logError('[AuthContext] Apple sign-in exception:', errStr, e);
      setError(errStr);
      setLoading(false);
      return { user: null, error: errStr };
    }
  }, []);

  /**
   * Shared tail for both email entry points. An unverified account is deliberately
   * NOT promoted to signed-in — the backend rejects its token, so doing so would
   * leave the app in a state where every request fails.
   */
  const applyEmailResult = useCallback(async (result: EmailAuthResult): Promise<EmailAuthResult> => {
    if (result.user) {
      setPendingVerification(null);
      await applyWebSignIn(result.user, { setUser, setLoading });
      return result;
    }

    if (result.pendingVerification) {
      setPendingVerification(result.pendingVerification);
      setLoading(false);
      return result;
    }

    // Clear any pending-verification state first: it can predate this attempt
    // (the cold-start peek sets it), and leaving it set would bounce the user
    // back into the verify step on the next render despite a plain failure.
    setPendingVerification(null);
    setError(result.error || 'Sign-in failed');
    setErrorCode(result.errorCode || null);
    setLoading(false);
    return result;
  }, []);

  const signUpEmail = useCallback(async (email: string, password: string, displayName?: string) => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    return applyEmailResult(await signUpWithEmail(email, password, displayName));
  }, [applyEmailResult]);

  const signInEmail = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    return applyEmailResult(await signInWithEmail(email, password));
  }, [applyEmailResult]);

  const sendPasswordReset = useCallback(async (email: string) => {
    setError(null);
    setErrorCode(null);
    const result = await sendReset(email);
    if (!result.ok) {
      setError(result.error || 'Could not send reset email');
      setErrorCode(result.errorCode || null);
    }
    return result;
  }, []);

  const resendVerification = useCallback(async () => {
    const result = await resendVerificationEmail();
    if (!result.ok) {
      setError(result.error || 'Could not resend the link');
      setErrorCode(result.errorCode || null);
    }
    return result;
  }, []);

  /**
   * Poll for verification. On success this promotes the user, which unmounts the
   * sign-in screen.
   *
   * The forced ID-token refresh that makes this work lives in
   * refreshVerificationStatus — see the trap documented there.
   */
  const checkVerification = useCallback(async () => {
    const result = await refreshVerificationStatus();
    if (result.user) {
      setPendingVerification(null);
      await applyWebSignIn(result.user, { setUser, setLoading });
      return { verified: true };
    }
    return { verified: false };
  }, []);

  const clearAuthError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  const clearPendingVerification = useCallback(() => {
    setPendingVerification(null);
    clearAuthError();
  }, [clearAuthError]);

  const signOut = useCallback(async () => {
    // Local session state FIRST: the UI lands on SignInScreen immediately and
    // can never be held hostage by a slow teardown step below (the native
    // bridge call alone has a 30s hang guard — awaiting it before setUser(null)
    // left the app looking signed in for that whole window). The last-known-good
    // entitlement belongs to the account that just left — drop it too. (It's
    // also uid-keyed, so this is defense in depth, not the only guard.)
    clearCachedEntitlement();
    setUser(null);
    setFallbackUser(null);
    // User-scoped app data (greeting name, intention, cached todos/prefs) also
    // belongs to the account that just left. AppContext's identity guard clears
    // it too when it sees user → null, but this storage-level sweep holds the
    // contract even if the page is torn down before React processes that
    // transition (e.g. window closed right after tapping Sign out). The
    // localStorage removals inside run synchronously; the extension-storage
    // mirror is best-effort like every teardown step below.
    void clearUserScopedStorageAsync();

    // Per-surface teardown — each step isolated so one failure can never skip
    // the others and leave a surface signed in.
    if (hasExtensionRuntime()) {
      try {
        await bgAuthBridge.signOut();
      } catch { /* non-fatal */ }
    }
    // iOS native app: also clear the native Firebase session + App Group keys.
    if (isNativeApp()) {
      try {
        await signOutNative();
      } catch { /* non-fatal */ }
    }
    try {
      await signOutUser();
    } catch { /* non-fatal */ }
  }, []);

  const deleteAccount = useCallback(async () => {
    // 1. Backend deletion needs the live session (Firebase ID token), so it
    //    runs first and throws to the caller on failure — never sign out a
    //    user whose account still exists.
    await billingApi.deleteAccount();

    // Everything past this point is best-effort: identity is already revoked
    // server-side, so a failed step must never strand a half-signed-in UI.

    // 2. Purge the deleted account's local data. Extension surfaces cache
    //    tasks/notes/settings in chrome.storage.local; the native app and web
    //    fall back to localStorage (services/storage mirrors both).
    try {
      if ((ext as any)?.storage?.local?.clear) {
        await new Promise<void>((resolve) => ext.storage.local.clear(() => resolve()));
      }
    } catch { /* non-fatal */ }
    try {
      window.localStorage.clear();
    } catch { /* non-fatal */ }

    // 3. End the session everywhere — background/service-worker auth, native
    //    Firebase session via the bridge, web Firebase session, localStorage
    //    fallback, entitlement cache. signOut sets user → null, which re-renders
    //    AppContent to SignInScreen with no navigation.
    await signOut();
  }, [signOut]);

  return (
    <AuthContext.Provider value={{
      user, loading, error, errorCode,
      signInGoogle, signInApple,
      signUpEmail, signInEmail, sendPasswordReset, resendVerification, checkVerification,
      pendingVerification, clearPendingVerification, clearAuthError,
      signOut, deleteAccount, isReady,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export default AuthContext;
